import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { InputNumber } from '@jsmdg/yoshi';
import { isBox, isExperience } from '../../../shared/product-safeguards';
import { VoucherQuantity } from '../../helper/voucherQuantity';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { trackVoucherCountSelection } from '../VoucherAndTicketSelector/VoucherCountSelection.tracking';
import styles from './DeliveryFormatQuantity.module.scss';

const messages = defineMessages({
    giftBoxQuantityLabel: {
        defaultMessage: 'Anzahl der Geschenkboxen',
    },
    voucherQuantityLabel: {
        defaultMessage: 'Anzahl der Gutscheine',
    },
    participants: {
        defaultMessage:
            'für {participantsCountLabel} {maxParticipants, plural, one {Person} other {Personen}}',
    },
    decreaseGiftBoxQuantity: {
        defaultMessage: 'Anzahl der Geschenkboxen verringern',
    },
    increaseGiftBoxQuantity: {
        defaultMessage: 'Anzahl der Geschenkboxen erhöhen',
    },
    decreaseVoucherQuantity: {
        defaultMessage: 'Gutscheinanzahl verringern',
    },
    increaseVoucherQuantity: {
        defaultMessage: 'Gutscheinanzahl erhöhen',
    },
});

function getParticipantsCountLabel(minParticipants: number, maxParticipants: number): string {
    if (!minParticipants || !maxParticipants) {
        return '';
    }

    if (minParticipants === maxParticipants) {
        return `${minParticipants}`;
    }

    return `${minParticipants}-${maxParticipants}`;
}

const DeliveryFormatQuantity = (): JSX.Element => {
    const intl = useIntl();
    const product = useProduct();
    const { price, setMultiplier } = usePrice();

    const isGiftBox = isBox(product);

    const { maxParticipants, minParticipants } =
        isBox(product) || isExperience(product)
            ? product
            : { maxParticipants: 0, minParticipants: 0 };

    const participantsCountLabel = getParticipantsCountLabel(minParticipants, maxParticipants);

    const quantityLabel = intl.formatMessage(
        isGiftBox ? messages.giftBoxQuantityLabel : messages.voucherQuantityLabel,
    );

    const updateQuantity = (nextQuantity: number): void => {
        setMultiplier(nextQuantity);
        void trackVoucherCountSelection(product.title, nextQuantity - price.multiplier);
    };

    return (
        <div
            className="d-flex align-items-center justify-content-between gap-2x mt-2x"
            data-testid="delivery-format-quantity"
        >
            <span className="d-flex flex-column">
                <span className={classNames('fw-bold', styles.quantityText)}>{quantityLabel}</span>
                {participantsCountLabel && (
                    <span className={classNames('fw-regular', styles.quantityText)}>
                        (
                        {intl.formatMessage(messages.participants, {
                            participantsCountLabel,
                            maxParticipants,
                        })}
                        )
                    </span>
                )}
            </span>
            <InputNumber
                value={price.multiplier}
                minValue={VoucherQuantity.Min}
                maxValue={VoucherQuantity.Max}
                onQuantityChange={updateQuantity}
                a11yDecrementText={intl.formatMessage(
                    isGiftBox ? messages.decreaseGiftBoxQuantity : messages.decreaseVoucherQuantity,
                )}
                a11yIncrementText={intl.formatMessage(
                    isGiftBox ? messages.increaseGiftBoxQuantity : messages.increaseVoucherQuantity,
                )}
                a11yValue={`${quantityLabel}: ${price.multiplier}`}
            />
        </div>
    );
};

export { DeliveryFormatQuantity };
